import { useMutation, useQuery } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { api, ApiError, fieldIssues } from '../api';
import { ConflictNotice } from '../ui/ConflictNotice';
import type { Me } from '../auth/me';
import { QUOTES_RESOURCE, type Quote } from './types';
import { DialogLayer } from '../ui/Dialog';
import { useUnsavedGuard } from '../ui/unsaved';

/** One right the person lacks, as the permission editor names it. */
export type MissingRight = { resource: string; right: string; note?: string };
type Check = { userId: string; name: string; missing: MissingRight[] };

/** Giving a quote to someone (P16, point 5): the owner or whoever edits every quote (scope "wszystkie"). */
export const mayDelegate = (me: Me): boolean => me.isOwner || me.permissions.some((p) => p.resource === QUOTES_RESOURCE && p.action === 'edit' && p.scope === 'all');

/** "„Kosztorysy” → „Edycja”" plus the note, for the list of what to grant. */
export const missingText = (m: MissingRight): string => `„${m.resource}” → ${m.right.startsWith('pole') ? m.right : `„${m.right}”`}${m.note ? ` (${m.note})` : ''}`;

/**
 * "Przekaż wycenę": choose the person responsible for the quote. On choosing, what they lack for a good quote is
 * listed by the permission editor's names; giving it is allowed anyway (the rights can be granted afterwards).
 * No "are you sure" (owner's rule for the quote).
 */
export function ResponsibleDialog({ quote, onDone, onClose, onReload }: { quote: Quote; onDone: () => void; onClose: () => void; onReload: () => void }) {
  const [userId, setUserId] = useState(quote.responsibleUserId ?? '');
  const people = useQuery({ queryKey: ['quotes', 'responsible-candidates'], queryFn: () => api<{ users: Array<{ id: string; name: string }> }>('/api/quotes/responsible-candidates') });
  const check = useQuery({
    queryKey: ['quotes', 'delegate-check', quote.id, userId],
    enabled: userId !== '',
    queryFn: () => api<Check>(`/api/quotes/quotes/${quote.id}/delegate-check?userId=${userId}`),
  });
  const save = useMutation({
    mutationFn: () => api(`/api/quotes/quotes/${quote.id}`, { method: 'PATCH', body: { responsibleUserId: userId === '' ? null : userId, version: quote.version } }),
    onSuccess: onDone,
  });
  function submit(e: FormEvent) {
    e.preventDefault();
    save.mutate();
  }
  // owner 2026-10-23 21:14: Escape or leaving with unsaved changes asks „Zapisz / Nie zapisuj / Anuluj”
  const guardRef = useUnsavedGuard({
    dirty: userId !== (quote.responsibleUserId ?? ''),
    save: async () => {
      await save.mutateAsync();
      return true;
    },
  });
  const error = save.error instanceof ApiError ? save.error : null;
  const missing = check.data?.missing ?? [];
  return (
    <div className="dialog-backdrop">
      <form ref={guardRef} className="dialog panel" role="dialog" aria-modal="true" aria-labelledby="responsible-title" onSubmit={submit} data-feature-id="quotes.responsible">
        <DialogLayer onClose={onClose} busy={save.isPending} />
        <h2 id="responsible-title">Odpowiedzialny za wycenę</h2>
        <p className="muted">Ta osoba zobaczy kosztorys u siebie w „Wycenach” i dostanie powiadomienie.</p>
        {error?.status === 409 && <ConflictNotice onReload={onReload} />}
        <label className="field">
          <span>Osoba</span>
          <select value={userId} onChange={(e) => setUserId(e.target.value)} autoFocus>
            <option value="">— nikt (tylko autor) —</option>
            {(people.data?.users ?? []).map((u) => (
              <option key={u.id} value={u.id}>
                {u.name}
              </option>
            ))}
          </select>
        </label>
        {userId !== '' && check.isLoading && (
          <p className="muted" role="status">
            Sprawdzam uprawnienia…
          </p>
        )}
        {userId !== '' && check.data && missing.length === 0 && (
          <p className="hint" role="status">
            {check.data.name} ma wszystko, czego potrzeba do wyceny.
          </p>
        )}
        {missing.length > 0 && (
          <div className="alert q-missing" role="status" aria-label="Brakujące uprawnienia">
            <p>
              {check.data?.name} nie ma uprawnień potrzebnych do dobrej wyceny. Nadaj w <strong>Ustawienia → Uprawnienia</strong> (rola albo wyjątek tej osoby):
            </p>
            <ul>
              {missing.map((m) => (
                <li key={`${m.resource}-${m.right}`}>{missingText(m)}</li>
              ))}
            </ul>
            <p>Możesz przekazać wycenę już teraz i nadać prawa później.</p>
          </div>
        )}
        {error && error.status !== 409 && (
          <p className="alert" role="alert">
            {fieldIssues(error)[0]?.message ?? error.message}
          </p>
        )}
        <div className="actions">
          <button className="btn primary" type="submit" disabled={save.isPending || userId === (quote.responsibleUserId ?? '')}>
            {userId === '' ? 'Zdejmij przekazanie' : 'Przekaż wycenę'}
          </button>
          <button className="btn" type="button" onClick={onClose}>
            Anuluj
          </button>
        </div>
      </form>
    </div>
  );
}
